import React from 'react';
import { reportError } from '../utils/errorReport';

/**
 * What the user sees when a screen breaks.
 *
 * React answers an error thrown while drawing by unmounting everything — the
 * blank page with no way out that the ＋ button produced yesterday. Nobody could
 * tell what had happened, including the person who wrote it, and the only way
 * out was to close the app.
 *
 * With this in place the same fault becomes: a sentence, a button that puts you
 * back on today's list, and a report that arrives without anybody having to
 * write in. The app carries on.
 *
 * Deliberately plain: no hooks, no translations from i18n, no api client.
 * Whatever has just broken, THIS must still render — so it depends on nothing
 * except React itself.
 */

const TEXT = {
  it: { title: 'Qualcosa si è rotto in questa schermata.', body: 'Non hai perso niente di salvato. Torna agli impegni di oggi e riprova.', home: 'Torna a Oggi' },
  en: { title: 'Something broke on this screen.', body: 'Nothing saved has been lost. Go back to today and try again.', home: 'Back to Today' },
  es: { title: 'Algo se ha roto en esta pantalla.', body: 'No has perdido nada de lo guardado. Vuelve a hoy e inténtalo de nuevo.', home: 'Volver a Hoy' },
  de: { title: 'Auf diesem Bildschirm ist etwas kaputtgegangen.', body: 'Es ist nichts Gespeichertes verloren. Geh zurück zu Heute und versuche es erneut.', home: 'Zurück zu Heute' },
  fr: { title: 'Quelque chose a cassé sur cet écran.', body: "Rien d'enregistré n'a été perdu. Revenez à aujourd'hui et réessayez.", home: "Retour à Aujourd'hui" },
  nl: { title: 'Er is iets stukgegaan op dit scherm.', body: 'Er is niets opgeslagens verloren. Ga terug naar vandaag en probeer opnieuw.', home: 'Terug naar Vandaag' },
  pt: { title: 'Algo se partiu neste ecrã.', body: 'Não perdeste nada do que foi guardado. Volta a hoje e tenta de novo.', home: 'Voltar a Hoje' },
  'pt-BR': { title: 'Algo quebrou nesta tela.', body: 'Você não perdeu nada do que foi salvo. Volte para hoje e tente de novo.', home: 'Voltar para Hoje' },
  ru: { title: 'На этом экране что-то сломалось.', body: 'Ничего сохранённого не потеряно. Вернитесь к сегодняшнему дню и попробуйте снова.', home: 'К сегодняшнему дню' },
  pl: { title: 'Coś się zepsuło na tym ekranie.', body: 'Nic zapisanego nie zginęło. Wróć do dzisiaj i spróbuj ponownie.', home: 'Wróć do Dziś' },
  ro: { title: 'Ceva s-a stricat pe acest ecran.', body: 'Nu s-a pierdut nimic din ce era salvat. Întoarce-te la azi și încearcă din nou.', home: 'Înapoi la Azi' },
  zh: { title: '此页面出现了问题。', body: '已保存的内容没有丢失。返回今天再试一次。', home: '返回今天' },
  ja: { title: 'この画面で問題が起きました。', body: '保存済みのものは失われていません。今日に戻ってやり直してください。', home: '今日に戻る' },
  hi: { title: 'इस स्क्रीन में कुछ टूट गया।', body: 'सहेजा हुआ कुछ भी नहीं खोया। आज पर लौटें और फिर कोशिश करें।', home: 'आज पर लौटें' },
  ko: { title: '이 화면에서 문제가 발생했습니다.', body: '저장된 내용은 사라지지 않았습니다. 오늘로 돌아가 다시 시도하세요.', home: '오늘로 돌아가기' },
  ar: { title: 'حدث خطأ في هذه الشاشة.', body: 'لم يُفقد أي شيء محفوظ. عد إلى اليوم وحاول مجددًا.', home: 'العودة إلى اليوم' },
};

function textFor() {
  // Read straight from the browser: a broken app may not have a user object.
  try {
    const nav = navigator.language || 'it';
    return TEXT[nav] || TEXT[nav.split('-')[0]] || TEXT.en;
  } catch (_) {
    return TEXT.en;
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { broken: false };
  }

  static getDerivedStateFromError() {
    return { broken: true };
  }

  componentDidCatch(error, info) {
    reportError('render', error, {
      // Which component tree, not what was in it.
      component: String(info?.componentStack || '').trim().split('\n')[0]?.trim().slice(0, 80) || '',
    });
  }

  render() {
    if (!this.state.broken) return this.props.children;
    const t = textFor();
    return (
      // Marked so the smoke test can see it. A page that has fallen back to
      // this screen still mounts children and still reports no crash, and
      // hc.js called that a pass while Settings was refusing to render.
      <div data-error-boundary="1" style={{ padding: '64px 24px', textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
        <div style={{ fontSize: '2.2rem', marginBottom: 14 }} aria-hidden="true">🌱</div>
        <p style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 8, color: 'var(--color-text)' }}>{t.title}</p>
        <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: 22 }}>{t.body}</p>
        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          style={{
            padding: '12px 22px', borderRadius: 10, cursor: 'pointer',
            border: '2px solid var(--color-primary)', background: 'var(--color-primary)',
            color: '#fff', fontWeight: 700, fontSize: '0.9rem', fontFamily: 'inherit',
          }}
        >
          {t.home}
        </button>
      </div>
    );
  }
}
